import React from 'react';

// Dışarıdan gelen FormationPoint tipini güncelleyelim.
export interface FormationPoint {
  x: number;
  y: number;
  width: number;
  height: number;
  layer: number;
  rotation: 'HORIZONTAL' | 'VERTICAL';
  palletIdx?: number;
}

// Yüklenen Ürün Detayı (productName yerine productId kullanılması daha güvenlidir)
interface LoadedItem {
    productName: string;
    quantity: number;
    palletIndex: number;
}

// Gruplanmış Ürün Tipi (Toplama işleminden sonraki nihai format)
interface GroupedItem {
    productName: string;
    totalQuantity: number;
}

interface TruckFormationProps {
  formationPoints: FormationPoint[];
  layer?: number;
  gap?: number;
  style?: React.CSSProperties;
  onBoxClick?: (point: FormationPoint, index: number) => void;
  selectedBoxIndex?: number;
  loadedItems?: LoadedItem[];
}

// 🔥 KISALTMA HELPER'I: Sıralı Baş Harfleri Garantili ve Net Kısaltma (DEĞİŞMEDİ)
const getShortProductName = (name: string, maxLength: number = 10): string => {
    // 1. Parantez içindeki bilgileri (genellikle gramaj) kaldır
    const cleanedName = name.replace(/\([^)]*\)/g, '').trim();

    // 2. Çok uzun değilse direkt döndür
    if (cleanedName.length <= maxLength) {
        return cleanedName;
    }

    // Orijinal sıradaki kelimeleri al (tek harfli kelimeleri atarak)
    const words = cleanedName.split(' ').filter(w => w.length > 1);

    if (words.length > 1) {
        // **ÖNCELİK 1: SIRALI BAŞ HARFLERİ KISALTMASI (Örn: ÖGBP)**
        const abbreviation = words.slice(0, 4).map(w => w.charAt(0)).join('');

        if (abbreviation.length > 2 && abbreviation.length <= maxLength) {
             return abbreviation.toUpperCase();
        }

        // **ÖNCELİK 2: YEDEK STRATEJİLER (Sırayı Bozmamak için güvenli)**
        const primaryWord = [...words].sort((a, b) => b.length - a.length)[0] || '';

        if (primaryWord.length > maxLength) {
            return primaryWord.substring(0, maxLength - 2).toUpperCase() + '..';
        }

        const firstInitial = words[0].charAt(0).toUpperCase();
        if (primaryWord.length + firstInitial.length + 1 <= maxLength) {
            return `${firstInitial} ${primaryWord.substring(0, maxLength - firstInitial.length - 1)}`;
        }
    }

    // Son çare: Karakter uzunluğuna göre kes ve '..' ekle
    return cleanedName.substring(0, maxLength - 2).trim().toUpperCase() + '..';
};


const TruckFormation: React.FC<TruckFormationProps> = ({
  formationPoints,
  layer = 1,
  gap = 5,
  style,
  onBoxClick,
  selectedBoxIndex,
  loadedItems = [],
}) => {

  const safePoints = Array.isArray(formationPoints) ? formationPoints : [];
  const pointsToRender = safePoints.filter(p => p.layer === layer);

  if (pointsToRender.length === 0) return null;

  // Paletlerin min/max koordinatlarını bul
  const minX = Math.min(...pointsToRender.map(p => p.x));
  const minY = Math.min(...pointsToRender.map(p => p.y));
  const maxX = Math.max(...pointsToRender.map(p => p.x + p.width));
  const maxY = Math.max(...pointsToRender.map(p => p.y + p.height));

  const formationWidth = maxX - minX;
  const formationHeight = maxY - minY;

  // ----------------------------------------------------
  // 🔥 YARDIMCI FONKSİYON: Palet içeriğini gruplayıp özetle (DEĞİŞMEDİ)
  // ----------------------------------------------------
  const getPalletContent = (palletIndex: number, boxHeight: number): { text: string, isSummary: boolean }[] => {

    const itemsInPallet = loadedItems.filter(item => item.palletIndex === palletIndex);

    if (itemsInPallet.length === 0) {
      return [{ text: `PALET ${palletIndex}`, isSummary: true }];
    }

    const groupedItemsMap = itemsInPallet.reduce((acc, item) => {
        const key = item.productName;
        if (!acc[key]) {
            acc[key] = { productName: item.productName, totalQuantity: 0 };
        }
        acc[key].totalQuantity += item.quantity;
        return acc;
    }, {} as Record<string, GroupedItem>);

    const groupedItems = Object.values(groupedItemsMap);

    const point = pointsToRender.find(p => p.palletIdx === palletIndex);
    const effectiveLength = point && point.width < 100 ? 8 : 12;

    const content = groupedItems.map(item => {
      const shortName = getShortProductName(item.productName, effectiveLength);
      return { text: `${shortName}: ${item.totalQuantity}`, isSummary: false };
    });

    const maxVisibleLines = Math.max(1, Math.floor((boxHeight - 4) / 12));
    const linesToShow = Math.min(maxVisibleLines, content.length);

    if (content.length > linesToShow) {
        const remainingCount = content.length - linesToShow;
        const totalQty = groupedItems.reduce((sum, item) => sum + item.totalQuantity, 0);
        return [
            ...content.slice(0, linesToShow - 1),
            { text: `+${remainingCount + 1} ÇEŞİT (${totalQty} ADET)`, isSummary: true }
        ];
    }

    return content;
  };
  // ----------------------------------------------------


  const truckStyle = {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    width: formationWidth + gap * 2,
    height: formationHeight + gap * 2,
    border: '3px solid #000',
    boxSizing: 'border-box' as const,
  };

  return (
    <div
      style={{
        position: 'relative',
        width: formationWidth + gap * 2,
        height: formationHeight + gap * 2,
        margin: '10px auto',
        backgroundColor: '#f8f8f8',
        ...style,
      }}
    >
      {/* Araç kasası */}
      <div style={truckStyle}></div>

      {/* Paletler */}
      {pointsToRender.map((p) => {
        const palletIndex = p.palletIdx ?? 0;

        const baseFontSize = p.height < 30 ? 7 : (p.height < 45 ? 8 : (p.height < 60 ? 9 : (p.height < 90 ? 10 : 12)));

        const isVertical = p.rotation === 'VERTICAL';

        const boxHeight = isVertical ? p.width : p.height;
        const contentLines = getPalletContent(palletIndex, boxHeight);


        // Rotasyon ayarı (dikey kutu için)
        const rotationStyle = isVertical
            ? {
                transform: p.rotation === 'VERTICAL' ? 'rotate(90deg)' : 'none',
                transformOrigin: 'top left',
                left: p.x - minX + gap,
                top: p.y - minY + gap,
                width: p.width,
                height: p.height,
              }
            : {
                transform: 'none',
                transformOrigin: 'top left',
                width: p.width,
                height: p.height,
                left: p.x - minX + gap,
                top: p.y - minY + gap,
              };

        // Kutu doluluk durumuna göre arka plan rengi
        const isLoaded = contentLines.some(c => c.text.startsWith('PALET') === false);
        const bgColor = palletIndex === selectedBoxIndex
          ? '#a8d2ff'
          : (isLoaded
             ? '#d4edda'
             : 'transparent');

        return (
          <div
            key={palletIndex}
            style={{
              position: 'absolute',
              border: '1px solid #000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: baseFontSize,
              padding: isVertical ? 2 : 1,
              overflow: 'hidden',
              lineHeight: 1.2,
              backgroundColor: bgColor,
              cursor: 'pointer',
              zIndex: 1,
              boxSizing: 'border-box',
              ...rotationStyle,
            }}
            onClick={() => onBoxClick && onBoxClick(p, palletIndex)}
          >
            {contentLines.map((line, idx) => (
              <span
                key={idx}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: line.isSummary ? 'bold' : 'normal',
                  fontSize: line.isSummary ? baseFontSize + 1 : baseFontSize,
                  textAlign: 'center',
                  maxWidth: '100%',
                }}
              >
                {line.text}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default TruckFormation;